import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { forkJoin } from "rxjs";
import { SubSink } from "subsink";
import { ChaseIdComponent } from "../../../../shared/chase-grid/chase-id.component";
import { BasicGridComponent } from "../../../../shared/grid/basic-grid/basic-grid.component";
import { GridPipeName } from "../../../../shared/grid/grid-pipe.enum";
import { GridService } from "../../../../shared/grid/grid.service";
import { GridColumnDefinition } from "../../../../shared/grid/models/grid-column-definition.model";
import { GridConfiguration } from "../../../../shared/grid/models/grid-configuration.model";
import { FeedbackChallengeQueryItem } from "../../member/chase-detail/chase-detail-chart/overread-feedback/feedback-challenge-query-item.model";
import { OverreadFeedbackService } from "../../member/chase-detail/chase-detail-chart/overread-feedback/overread-feedback.service";
import { ReviewDeadlineState } from "../../member/chase-detail/chase-detail-chart/overread-feedback/review-deadline";
import { StandardFilterType } from "../../member/chase-detail/chase-detail-chart/overread-feedback/standard-filter-type.enum";

const DATE_TIME_FORMAT = "MM-dd-yyyy hh:mm a";

@Component({
  selector: "app-clinical-feedback-challenges",
  templateUrl: "./clinical-feedback-challenges.component.html",
  styleUrls: ["./clinical-feedback-challenges.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClinicalFeedbackChallengesComponent implements OnInit, OnDestroy {
  @ViewChild("chaseIdColumn", { static: true }) chaseIdColumn: TemplateRef<ChaseIdComponent>;
  @ViewChild("feedbackStatusColumn", { static: true }) feedbackStatusColumn: TemplateRef<any>;
  @ViewChild("daysLeftColumn", { static: true }) daysLeftColumn: TemplateRef<any>;
  @ViewChild(BasicGridComponent, { static: true }) basicGridComponent: BasicGridComponent;
  readonly chaseIdRouteUrl = "/members/chase/:chaseId";
  private sink = new SubSink();
  private midnightRefresh: ReturnType<typeof setTimeout>;
  private tabData: FeedbackChallengeQueryItem[] = [];
  isComplete = false;
  gridData: FeedbackChallengeQueryItem[] = [];
  gridConfiguration: GridConfiguration;
  stateFilter: ReviewDeadlineState | null = null;
  readonly stateFilterOptions = [
    { label: "All", value: null },
    { label: ReviewDeadlineState.ON_TRACK, value: ReviewDeadlineState.ON_TRACK },
    { label: ReviewDeadlineState.DUE_SOON, value: ReviewDeadlineState.DUE_SOON },
    { label: ReviewDeadlineState.DUE_TODAY, value: ReviewDeadlineState.DUE_TODAY },
  ];

  constructor(
    private readonly router: Router,
    private readonly overreadFeedbackService: OverreadFeedbackService,
    private readonly gridService: GridService,
    private readonly changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadTab(false);
    this.scheduleMidnightRefresh();
  }

  ngOnDestroy(): void {
    this.sink.unsubscribe();
    clearTimeout(this.midnightRefresh);
  }

  loadTab(isComplete: boolean): void {
    this.isComplete = isComplete;
    if (isComplete) {
      this.stateFilter = null;
    }
    this.gridConfiguration = this.createGridConfiguration();
    this.sink.add(
      this.overreadFeedbackService.getFeedbackChallengeList(isComplete ? StandardFilterType.MANAGER_VIEW_COMPLETE : StandardFilterType.MANAGER_VIEW_INCOMPLETE)
        .subscribe(data => {
          this.tabData = data ?? [];
          this.applyStateFilter();
        })
    );
  }

  exportAll(): void {
    this.sink.add(
      forkJoin([
        this.overreadFeedbackService.getFeedbackChallengeList(StandardFilterType.MANAGER_VIEW_INCOMPLETE),
        this.overreadFeedbackService.getFeedbackChallengeList(StandardFilterType.MANAGER_VIEW_COMPLETE),
      ]).subscribe(([incomplete, complete]) => {
        const primeGrid = this.basicGridComponent.primeGrid;
        primeGrid.value = [...(incomplete ?? []), ...(complete ?? [])];
        primeGrid.exportCSV();
        primeGrid.value = this.gridData;
      })
    );
  }

  applyStateFilter(): void {
    this.gridData = this.stateFilter == null ? this.tabData : this.tabData.filter(item => item.reviewDeadlineState === this.stateFilter);
    this.changeDetector.markForCheck();
  }

  daysLeftClass(rowData: FeedbackChallengeQueryItem): string {
    switch (rowData.reviewDeadlineState) {
      case ReviewDeadlineState.DUE_TODAY:
        return "days-left--due-today";
      case ReviewDeadlineState.DUE_SOON:
        return "days-left--due-soon";
      default:
        return "days-left--on-track";
    }
  }

  reviewChallenge(rowData: FeedbackChallengeQueryItem): void {
    const url = this.gridService.generateLink(
      new GridColumnDefinition({ routeUrl: this.chaseIdRouteUrl } as any),
      rowData
    );
    this.router.navigateByUrl(`/${url}`);
  }

  get completeBtnClass(): string {
    return this.isComplete ? "active-button" : "";
  }

  get incompleteBtnClass(): string {
    return this.isComplete ? "" : "active-button";
  }

  private createGridConfiguration(): GridConfiguration {
    const columns = [
      new GridColumnDefinition({ field: "chaseId", template: this.chaseIdColumn, header: "Chase ID", width: "135px" }),
      new GridColumnDefinition({ field: "projectName", header: "Project" }),
      new GridColumnDefinition({ field: "codingUserName", header: "Coder / Abstractor Name" }),
      new GridColumnDefinition({ field: "codingDate", header: "Date & Time Submitted", pipeName: GridPipeName.Date, format: DATE_TIME_FORMAT, timeZone: "local" }),
      new GridColumnDefinition({ field: "qaUserName", header: "Overreader Name" }),
      new GridColumnDefinition({ field: "overreadLevel", header: "Overread Level" }),
      new GridColumnDefinition({ field: "qaDate", header: "Date & Time Overread", pipeName: GridPipeName.Date, format: DATE_TIME_FORMAT, timeZone: "local" }),
      new GridColumnDefinition({ field: "errorCount", header: "# of Errors Found" }),
      new GridColumnDefinition({ field: "daysLeft", template: this.daysLeftColumn, header: "Days Left", width: "170px", show: !this.isComplete }),
      new GridColumnDefinition({ field: "chaseOverreadFeedbackStatus", header: "Manager Review" }),
      new GridColumnDefinition({ template: this.feedbackStatusColumn, isSortableColumn: false, header: "Feedback Status", width: "170px" }),
    ];
    return new GridConfiguration({
      columns,
      pageSize: 25,
      showExportAction: false,
      showActionColumn: false,
      pageSizeOptions: [10, 25, 50, 100],
    });
  }

  private scheduleMidnightRefresh(): void {
    const now = new Date();
    const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    this.midnightRefresh = setTimeout(() => {
      this.loadTab(this.isComplete);
      this.scheduleMidnightRefresh();
    },                                nextMidnight.getTime() - now.getTime());
  }
}
